/**
 * Web Push Notification utilities for AutoBook Pro.
 * Handles Service Worker registration, push subscription, and permission requests.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function getAuth() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}` } };
}

// Convert VAPID key from URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Detect device type
function getDeviceType() {
  const ua = navigator.userAgent || '';
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return 'mobile';
  return 'desktop';
}

/**
 * Register Service Worker and subscribe to push notifications.
 * Call this after user logs in.
 */
export async function initPushNotifications() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('Push notifications not supported in this browser');
    return false;
  }

  if (!VAPID_PUBLIC_KEY) {
    console.log('VAPID public key not configured');
    return false;
  }

  try {
    // Register service worker
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    console.log('Service Worker registered');

    // Wait for it to be ready
    const sw = await navigator.serviceWorker.ready;

    // Check existing subscription
    let subscription = await sw.pushManager.getSubscription();

    if (!subscription) {
      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return false;
      }

      // Subscribe to push
      subscription = await sw.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      console.log('Push subscription created');
    }

    // Send subscription to backend
    const response = await fetch(`${API}/api/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuth().headers,
      },
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        device_type: getDeviceType(),
      }),
    });

    if (response.ok) {
      console.log('Push subscription sent to server');
      return true;
    }
    return false;
  } catch (err) {
    console.error('Push notification setup failed:', err);
    return false;
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribePush() {
  try {
    const sw = await navigator.serviceWorker.ready;
    const subscription = await sw.pushManager.getSubscription();
    if (subscription) {
      await fetch(`${API}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuth().headers,
        },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
      await subscription.unsubscribe();
      console.log('Unsubscribed from push');
    }
  } catch (err) {
    console.error('Push unsubscribe failed:', err);
  }
}

/**
 * Check if push is supported and user has granted permission.
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

export function isPushPermissionGranted() {
  return 'Notification' in window && Notification.permission === 'granted';
}
